import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Grid,
  Card,
  Typography,
  Avatar,
  Grow,
  Alert,
} from "@mui/material";
import {
  PhotoCamera as PhotoCameraIcon,
  Timeline as TimelineIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import {
  fetchStats,
  selectStats,
  selectStatsLoading,
  selectStatsError,
} from "./statsSlice";

export default function Stats() {
  const dispatch = useDispatch();
  const stats = useSelector(selectStats);
  const loading = useSelector(selectStatsLoading);
  const error = useSelector(selectStatsError);

  useEffect(() => {
    dispatch(fetchStats());
  }, [dispatch]);

  const statsCards = [
    {
      label: "Active Cameras",
      value: stats.cameras,
      icon: <PhotoCameraIcon />,
      color: "#4CAF50",
    },
    {
      label: "Total Events",
      value: stats.events,
      icon: <TimelineIcon />,
      color: "#2196F3",
    },
    {
      label: "Species Detected",
      value: stats.species,
      icon: <LocationIcon />,
      color: "#FF9800",
    },
    {
      label: "Active Users",
      value: stats.users,
      icon: <PeopleIcon />,
      color: "#9C27B0",
    },
  ];

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }} id="stats">
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load statistics: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 6 }} id="stats">
      <Grow in timeout={600}>
        <Box>
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 2,
              color: "#8e8e8e",
              fontSize: { xs: "2rem", md: "3rem" },
              textShadow: "0 2px 20px rgba(0,0,0,0.3)",
            }}
          >
            System Overview
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 5,
              color: "rgba(104, 101, 101, 0.7)",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Real-time tracking and monitoring of wildlife across protected areas
          </Typography>

          <Grid container spacing={3}>
            {statsCards.map((stat, index) => (
              <Grid item xs={6} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderRadius: 4,
                    transition: "all 0.4s ease",
                    background:
                      "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(45,45,45,0.95) 100%)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      transform: "translateY(-12px) scale(1.02)",
                      boxShadow: `0 20px 60px ${stat.color}40`,
                      borderColor: stat.color,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      mx: "auto",
                      mb: 2,
                      bgcolor: stat.color,
                      width: 64,
                      height: 64,
                      boxShadow: `0 8px 30px ${stat.color}50`,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Typography
                    variant="h3"
                    fontWeight={700}
                    sx={{ color: "#fff" }}
                  >
                    {loading ? (
                      <Box
                        sx={{
                          display: "inline-block",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      >
                        ...
                      </Box>
                    ) : (
                      `${stat.value}+`
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    {stat.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Add CSS animation for loading state */}
          <style jsx="true">{`
            @keyframes pulse {
              0% {
                opacity: 0.3;
              }
              50% {
                opacity: 1;
              }
              100% {
                opacity: 0.3;
              }
            }
          `}</style>
        </Box>
      </Grow>
    </Container>
  );
}
